/** Shared types + role constants for the org team management surface. */

export const ORG_ROLE_LABELS = {
  'org:admin': 'Admin',
  'org:member': 'Member',
  'org:hunts_admin': 'Hunts Admin',
} as const;

export type OrgRole = keyof typeof ORG_ROLE_LABELS;

/** Legacy bare role values still present on old membership rows. */
const LEGACY_ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  member: 'Member',
};

export function roleLabel(role: string): string {
  return (
    ORG_ROLE_LABELS[role as OrgRole] ??
    LEGACY_ROLE_LABELS[role] ??
    role.replace(/^org:/, '').replace(/_/g, ' ')
  );
}

/** Roles that may manage members/invitations (mirrors the server rule). */
export function isAdminRole(role: string | undefined): boolean {
  return role === 'org:admin' || role === 'admin';
}

/**
 * Roles offered in pickers for the active org. `org:hunts_admin` (external
 * hunts collaborator) only exists inside the internal Destination Tours org.
 */
export function assignableRoles(isInternalOrg: boolean): OrgRole[] {
  return isInternalOrg
    ? ['org:admin', 'org:member', 'org:hunts_admin']
    : ['org:admin', 'org:member'];
}

export interface TeamMemberDto {
  userId: string;
  name: string | null;
  email: string | null;
  imageUrl: string | null;
  role: string;
  joinedAt: number;
  isSelf: boolean;
}

export interface MembersResponse {
  success: boolean;
  data: {
    members: TeamMemberDto[];
    callerRole: string;
    isInternalOrg: boolean;
  };
}

export interface TeamInvitationDto {
  id: string;
  email: string;
  role: string;
  status: string;
  createdAt: number;
  expiresAt: number | null;
}

export interface InvitationsResponse {
  success: boolean;
  data: TeamInvitationDto[];
}

export interface CreateInvitationResponse {
  success: boolean;
  data: TeamInvitationDto & { rolePinned: boolean };
}

export const MEMBERS_QUERY_KEY = ['OrganizationMembers'] as const;
export const INVITATIONS_QUERY_KEY = ['OrganizationInvitations'] as const;

/**
 * Toast copy when the server pinned an external invitee to Hunts Admin.
 * Scoped to the Destination Tours org on purpose — the rule does NOT apply to
 * other organizations, whose members get their selected role.
 */
export const ROLE_PINNED_MESSAGE =
  'People outside @destinationtours.com join the Destination Tours organization as Hunts Admin (hunts access only)';
